import { useState } from "react";
import { Link } from "react-router-dom";
import { MOCK_VENUES } from "@/lib/mockData";
import { ArrowLeft, QrCode, Building2, TrendingUp, Users } from "lucide-react";

const TABS = ["Dashboard", "Venue", "Deals", "Check-ins"];

const Business = () => {
  const [tab, setTab] = useState("Dashboard");
  const venue = MOCK_VENUES[0];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="business-page">
      <header className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="business-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-heading font-black text-xl tracking-tighter">GRABB / VENUES</span>
          </Link>
          <span className="brutal-border bg-[#C8E6C9] rounded-full px-3 py-1 text-xs font-bold uppercase flex items-center gap-1.5">
            <Building2 className="w-3 h-3" /> {venue.name}
          </span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KPI icon={Users} label="Check-ins (30d)" value="328" />
          <KPI icon={Users} label="Unique visitors" value="214" />
          <KPI icon={TrendingUp} label="Active deals" value="1" />
          <KPI icon={QrCode} label="QR scans" value="412" />
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`brutal-border rounded-full px-4 py-2 text-sm font-bold whitespace-nowrap ${
                tab === t ? "bg-[#0A0A0A] text-white brutal-shadow-sm" : "bg-white"
              }`}
              data-testid={`business-tab-${t.toLowerCase()}`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Dashboard" && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="brutal-card p-5">
              <h3 className="label-mono mb-3">Your QR code</h3>
              <div className="w-full aspect-square brutal-border rounded-xl bg-white flex items-center justify-center">
                <QrCode className="w-32 h-32" strokeWidth={1} />
              </div>
              <p className="text-xs text-[#555555] mt-3">Print and place at the counter. Customers scan to check in and redeem deals.</p>
            </div>
            <div className="brutal-card p-5">
              <h3 className="label-mono mb-3">Recent check-ins</h3>
              <div className="space-y-3">
                {[
                  { name: "Mia", t: "2m ago" },
                  { name: "Sam", t: "15m ago" },
                  { name: "Jordan", t: "44m ago" },
                  { name: "Riley", t: "1h ago" },
                ].map((c, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="font-bold">{c.name}</span>
                    <span className="text-[#555555] font-mono text-xs">{c.t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "Venue" && (
          <div className="brutal-card p-5 space-y-3 max-w-xl">
            <Field label="Name" value={venue.name} testid="business-venue-name" />
            <Field label="Category" value={venue.category} testid="business-venue-category" />
            <Field label="Address" value={venue.address} testid="business-venue-address" />
            <button className="brutal-btn bg-[#FF6A2C] text-white px-5 py-2.5 mt-2" data-testid="business-venue-save">Save</button>
          </div>
        )}

        {tab === "Deals" && (
          <div className="brutal-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="label-mono">Active deals</h3>
              <button className="brutal-btn bg-[#FF6A2C] text-white px-3 py-1.5 text-xs" data-testid="business-add-deal">+ New deal</button>
            </div>
            {venue.deal ? (
              <div className="brutal-border bg-[#FFCC00] rounded-lg p-3 flex items-center justify-between" data-testid="business-deal-row">
                <div>
                  <div className="font-bold">{venue.deal.label}</div>
                  <div className="text-xs text-[#555555]">Min group: 3 • Expires in 14 days</div>
                </div>
                <button className="brutal-btn bg-white px-3 py-1 text-xs" data-testid="business-deal-edit">Edit</button>
              </div>
            ) : (
              <div className="text-sm text-[#555555]">No deals yet. Add one to drive group visits.</div>
            )}
          </div>
        )}

        {tab === "Check-ins" && (
          <div className="brutal-card p-5">
            <h3 className="label-mono mb-3">All check-ins</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="label-mono pb-2 text-[#555555] border-b border-[#E0E0E0]">User</th>
                  <th className="label-mono pb-2 text-[#555555] border-b border-[#E0E0E0]">Time</th>
                  <th className="label-mono pb-2 text-[#555555] border-b border-[#E0E0E0]">Deal</th>
                </tr>
              </thead>
              <tbody>
                {["Mia", "Sam", "Jordan", "Riley", "Alex", "Casey"].map((n, i) => (
                  <tr key={n} className="border-b border-[#E0E0E0] last:border-0">
                    <td className="py-3 font-bold">{n}</td>
                    <td className="py-3 font-mono text-xs">{i * 12 + 2}m ago</td>
                    <td className="py-3 text-xs">{venue.deal?.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value }) => (
  <div className="brutal-card p-4">
    <Icon className="w-4 h-4 mb-2" />
    <div className="font-heading font-black text-2xl leading-none">{value}</div>
    <div className="label-mono text-[#555555] mt-1">{label}</div>
  </div>
);

const Field = ({ label, value, testid }) => (
  <div>
    <label className="label-mono block mb-1.5">{label}</label>
    <input defaultValue={value} className="brutal-input" data-testid={testid} />
  </div>
);

export default Business;
